import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * LanguageContext — the active UI language (English / Swahili).
 *
 * This is a benign UI preference, mirroring the theme preference (ADR-013). It
 * is NOT auth state and does not touch cookies or tokens, so it does not
 * conflict with ADR-001 (cookie-only auth). Persisted in `localStorage` so the
 * choice survives reloads.
 *
 * The two translation resources (`en.json`, `sw.json`) already exist and are
 * fully mirrored; this context simply selects which one `useTranslation`
 * resolves against.
 */

export type Language = 'en' | 'sw';

const STORAGE_KEY = 'securelabs.lang';
const DEFAULT_LANG: Language = 'en';

function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'sw';
}

function readPersisted(): Language {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isLanguage(raw) ? raw : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => readPersisted());

  // Keep <html lang> in sync for assistive tech and correct pronunciation.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode) — in-memory only, acceptable */
    }
  }, []);

  const value = useMemo<LanguageContextValue>(() => ({ lang, setLang }), [lang, setLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within <LanguageProvider>');
  return ctx;
}
